'use client';

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';

type Rail = { element: HTMLElement; host: HTMLElement; id: string; label: string; left: number; top: number; width: number; previous: boolean; next: boolean; rtl: boolean };
type Watched = { id: string; originalId: string | null; originalTabIndex: string | null; children: Set<Element>; selected: Element | null };

/** Progressive enhancement for CSS scrollers, including server-rendered tables
 * and rows added after navigation. No wrappers or reparenting of application DOM.
 * Existing story/category controls opt out with data-horizontal-scroll="managed".
 */
export function HorizontalScrollPolish() {
  const [rails, setRails] = useState<Rail[]>([]);
  useEffect(() => {
    const watched = new Map<HTMLElement, Watched>();
    const pending = new Set<HTMLElement>();
    let frame = 0, sequence = 0, disposed = false;
    const owned = (element: HTMLElement) => Boolean(element.closest('[data-horizontal-scroll-controls]'));
    const schedule = () => { if (!frame && !disposed) frame = requestAnimationFrame(flush); };
    const resize = new ResizeObserver(schedule);
    const restore = (element: HTMLElement, entry: Watched) => {
      resize.unobserve(element);
      entry.children.forEach(child => resize.unobserve(child));
      element.removeAttribute('data-horizontal-scroll-viewport');
      element.removeAttribute('data-horizontal-scroll-only');
      if (!entry.originalId && element.id === entry.id) element.removeAttribute('id');
      if (entry.originalTabIndex === null && element.getAttribute('tabindex') === '0') element.removeAttribute('tabindex');
      watched.delete(element);
    };
    const discover = (root: HTMLElement) => {
      if (owned(root)) return;
      for (const element of [root, ...root.querySelectorAll<HTMLElement>('*')]) {
        if (!(element instanceof HTMLElement) || owned(element) || element === document.body || /^(INPUT|TEXTAREA|SELECT|IFRAME)$/.test(element.tagName)) continue;
        const style = getComputedStyle(element);
        if (!['auto', 'scroll'].includes(style.overflowX) || element.dataset.horizontalScroll === 'managed') {
          const entry = watched.get(element);
          if (entry) restore(element, entry);
          continue;
        }
        let entry = watched.get(element);
        if (!entry) {
          const originalId = element.getAttribute('id');
          let id = originalId;
          while (!id || !originalId && document.getElementById(id)) id = `horizontal-scroll-${++sequence}`;
          entry = { id, originalId, originalTabIndex: element.getAttribute('tabindex'), children: new Set(), selected: null };
          watched.set(element, entry);
          resize.observe(element);
        }
        // A track, table or image can grow without changing its viewport.
        for (const child of entry.children) if (child.parentElement !== element) { resize.unobserve(child); entry.children.delete(child); }
        for (const child of element.children) if (!entry.children.has(child)) { entry.children.add(child); resize.observe(child); }
      }
    };
    const reveal = (element: HTMLElement, target: Element) => {
      const area = element.getBoundingClientRect(), box = target.getBoundingClientRect();
      const inset = Math.min(44, area.width / 5);
      const delta = box.width > area.width - inset * 2 ? box.left - area.left : box.left < area.left + inset ? box.left - area.left - inset : box.right > area.right - inset ? box.right - area.right + inset : 0;
      if (Math.abs(delta) > 1) element.scrollBy({ left: delta, behavior: 'instant' });
    };
    function flush() {
      frame = 0;
      for (const root of pending) if (root.isConnected) discover(root);
      pending.clear();
      const nextRails: Rail[] = [];
      for (const [element, entry] of watched) {
        if (!element.isConnected) { restore(element, entry); continue; }
        const style = getComputedStyle(element);
        if (!['auto', 'scroll'].includes(style.overflowX)) { restore(element, entry); continue; }
        const overflow = element.clientWidth > 0 && element.scrollWidth > element.clientWidth + 2;
        if (!overflow) {
          element.removeAttribute('data-horizontal-scroll-viewport');
          element.removeAttribute('data-horizontal-scroll-only');
          if (entry.originalTabIndex === null && element.getAttribute('tabindex') === '0') element.removeAttribute('tabindex');
          continue;
        }
        element.id = entry.id;
        element.setAttribute('data-horizontal-scroll-viewport', '');
        element.toggleAttribute('data-horizontal-scroll-only', element.scrollHeight <= element.clientHeight + 2);
        if (entry.originalTabIndex === null && !element.matches('nav,[role="tablist"]')) element.setAttribute('tabindex', '0');
        const selected = element.querySelector('[aria-selected="true"],[aria-current]:not([aria-current="false"]),[data-active="true"],[data-state="active"]');
        if (selected && selected !== entry.selected) reveal(element, selected);
        entry.selected = selected;
        const box = element.getBoundingClientRect();
        let left = Math.max(0, box.left), right = Math.min(document.documentElement.clientWidth, box.right);
        let top = Math.max(0, box.top), bottom = Math.min(window.innerHeight, box.bottom);
        // Keep controls inside nested scrollports, rather than over adjacent UI.
        for (let ancestor = element.parentElement; ancestor && ancestor !== document.body; ancestor = ancestor.parentElement) {
          const css = getComputedStyle(ancestor), bounds = ancestor.getBoundingClientRect();
          if (css.overflowX !== 'visible') { left = Math.max(left, bounds.left); right = Math.min(right, bounds.right); }
          if (css.overflowY !== 'visible') { top = Math.max(top, bounds.top); bottom = Math.min(bottom, bounds.bottom); }
        }
        if (right - left < 90 || bottom - top < 32 || style.visibility === 'hidden' || element.closest('[inert],[aria-hidden="true"]')) continue;
        const rtl = style.direction === 'rtl', offset = Math.abs(element.scrollLeft), max = element.scrollWidth - element.clientWidth;
        const label = element.getAttribute('aria-label') || element.getAttribute('aria-labelledby')?.split(/\s+/).map(id => document.getElementById(id)?.textContent?.trim()).filter(Boolean).join(' ') || (element.querySelector('table') ? 'Table' : element.tagName === 'PRE' ? 'Code example' : 'Content');
        const host = element.closest<HTMLElement>('dialog,[role="dialog"],[role="alertdialog"],[popover]') || document.body;
        // Tall viewports need controls beside the visible content, not over the
        // first row of a spreadsheet or its sticky sorting headers.
        let cueTop = top + (bottom - top) / 2, cueWidth = right - left;
        // Modal content often creates a fixed-position containing block through
        // transform. Portal inside the focus trap and convert viewport geometry.
        for (let ancestor: HTMLElement | null = host; ancestor && ancestor !== document.body; ancestor = ancestor.parentElement) {
          const css = getComputedStyle(ancestor);
          if (css.transform !== 'none' || css.translate !== 'none' || css.rotate !== 'none' || css.scale !== 'none' || css.perspective !== 'none' || css.filter !== 'none' || /paint|layout|strict|content/.test(css.contain) || /transform|filter/.test(css.willChange)) {
            const bounds = ancestor.getBoundingClientRect(), scaleX = bounds.width / ancestor.offsetWidth || 1, scaleY = bounds.height / ancestor.offsetHeight || 1;
            left = (left - bounds.left) / scaleX - ancestor.clientLeft + ancestor.scrollLeft;
            cueTop = (cueTop - bounds.top) / scaleY - ancestor.clientTop + ancestor.scrollTop;
            cueWidth /= scaleX;
            break;
          }
        }
        nextRails.push({ element, host, id: entry.id, label, left, top: cueTop, width: cueWidth, previous: offset > 2, next: max - offset > 2, rtl });
      }
      setRails(current => current.length === nextRails.length && current.every((rail, index) => {
        const next = nextRails[index];
        return rail.element === next.element && rail.host === next.host && rail.label === next.label && rail.left === next.left && rail.top === next.top && rail.width === next.width && rail.previous === next.previous && rail.next === next.next && rail.rtl === next.rtl;
      }) ? current : nextRails);
    }
    const mutations = new MutationObserver(records => {
      for (const record of records) {
        const target = record.target instanceof HTMLElement ? record.target : record.target.parentElement;
        if (!target || owned(target)) continue;
        if (record.type === 'attributes' && ['id', 'tabindex'].includes(record.attributeName || '')) continue;
        for (const node of record.addedNodes) if (node instanceof HTMLElement && !owned(node)) pending.add(node);
        if (record.type === 'attributes' && ['class', 'style', 'data-horizontal-scroll'].includes(record.attributeName || '')) pending.add(target);
        for (let ancestor: HTMLElement | null = target; ancestor && ancestor !== document.body; ancestor = ancestor.parentElement) if (watched.has(ancestor)) pending.add(ancestor);
      }
      schedule();
    });
    mutations.observe(document.body, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['class', 'style', 'hidden', 'open', 'aria-hidden', 'aria-label', 'aria-selected', 'aria-current', 'data-active', 'data-state', 'data-horizontal-scroll'] });
    const focus = (event: FocusEvent) => {
      if (!(event.target instanceof HTMLElement) || owned(event.target)) return;
      for (let ancestor = event.target.parentElement; ancestor; ancestor = ancestor.parentElement) if (watched.has(ancestor)) reveal(ancestor, event.target);
      schedule();
    };
    const keyboard = (event: KeyboardEvent) => {
      const element = event.target;
      if (!(element instanceof HTMLElement) || !watched.has(element) || !element.hasAttribute('data-horizontal-scroll-viewport') || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      const rtl = getComputedStyle(element).direction === 'rtl';
      if (event.key === 'Home' || event.key === 'End') element.scrollTo({ left: event.key === 'Home' ? 0 : (rtl ? -1 : 1) * element.scrollWidth, behavior: 'instant' });
      else element.scrollBy({ left: (event.key === 'ArrowLeft' ? -1 : 1) * element.clientWidth * .75, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    };
    const viewport = () => { pending.add(document.body); schedule(); };
    window.addEventListener('resize', viewport);
    document.addEventListener('scroll', schedule, true);
    document.addEventListener('focusin', focus);
    document.addEventListener('keydown', keyboard);
    document.addEventListener('load', viewport, true);
    document.addEventListener('animationend', schedule, true);
    document.addEventListener('transitionend', schedule, true);
    window.visualViewport?.addEventListener('resize', viewport);
    window.visualViewport?.addEventListener('scroll', schedule);
    void document.fonts.ready.then(() => { if (!disposed) viewport(); });
    pending.add(document.body); schedule();
    return () => {
      disposed = true;
      mutations.disconnect(); resize.disconnect(); cancelAnimationFrame(frame);
      window.removeEventListener('resize', viewport);
      document.removeEventListener('scroll', schedule, true);
      document.removeEventListener('focusin', focus);
      document.removeEventListener('keydown', keyboard);
      document.removeEventListener('load', viewport, true);
      document.removeEventListener('animationend', schedule, true);
      document.removeEventListener('transitionend', schedule, true);
      window.visualViewport?.removeEventListener('resize', viewport);
      window.visualViewport?.removeEventListener('scroll', schedule);
      for (const [element, entry] of watched) restore(element, entry);
    };
  }, []);
  return rails.map(rail => createPortal(<div data-horizontal-scroll-controls role="group" aria-label={`${rail.label} scroll controls`} className="horizontal-scroll-controls" style={{ left: rail.left, top: rail.top, width: rail.width }}>
    {([-1, 1] as const).map(direction => {
      const previous = rail.rtl ? direction === 1 : direction === -1;
      const enabled = previous ? rail.previous : rail.next;
      return <button key={direction} type="button" className="horizontal-scroll-cue" data-edge={direction === -1 ? 'left' : 'right'} disabled={!enabled} aria-label={`${previous ? 'Previous' : 'More'} ${rail.label}`} aria-controls={rail.id} title={previous ? 'Scroll back' : 'Scroll further'} onClick={() => rail.element.scrollBy({ left: direction * Math.max(60, rail.element.clientWidth * .75), behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' })}>{direction === -1 ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}</button>;
    })}
  </div>, rail.host, rail.id));
}
